import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import GrowVerticalIcon from '@atlaskit/icon/core/grow-vertical';
import { ButtonItem } from '@atlaskit/menu';
import {
	ButtonMenuItem,
	COLLAPSE_ELEM_BEFORE,
	FlyoutMenuItem,
	FlyoutMenuItemContent,
	FlyoutMenuItemTrigger,
	MenuList,
} from '@atlaskit/navigation-system';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { token } from '@atlaskit/tokens';

import type { NavItemReorderingProps, NavItemReorderingPropsOld } from '../../../types';

import messages from './messages';

function NavItemReorderingMenuOld({
	TriggerComponent = ButtonItem,
	canMoveUp,
	canMoveDown,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	onOpenChange,
}: NavItemReorderingPropsOld) {
	const { formatMessage } = useIntl();
	const isOnly = !canMoveUp && !canMoveDown;
	const [open, setOpen] = useState(false);

	const onOpenChangeInternal = useCallback(
		(args: OnOpenChangeArgs) => {
			setOpen(args.isOpen);
			onOpenChange?.(args);
		},
		[onOpenChange],
	);

	const iconProps = useMemo(() => {
		if (TriggerComponent === ButtonItem) {
			return {
				iconBefore: <GrowVerticalIcon label="" />,
				iconAfter: <ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />,
			};
		}

		return {
			elemBefore: <GrowVerticalIcon label="" />,
			elemAfter: <ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />,
		};
	}, [TriggerComponent]);

	if (TriggerComponent === FlyoutMenuItemTrigger) {
		if (isOnly) {
			// FlyoutMenuItemTrigger doesn't support isDisabled prop,
			// so if the trigger should be disabled we need to render something visually compatible that does support that state
			return (
				<ButtonMenuItem {...iconProps} isDisabled={true}>
					{formatMessage(messages.reorderingMenuTriggerLabel)}
				</ButtonMenuItem>
			);
		}

		// MenuList -> ButtonMenuItem is the closest visually to DropdownItemGroup -> DropdownItem, but they have different role attributes which is not ideal.
		// This is fixed under feature gates in the NavItemReorderingMenuNew version of this component.
		return (
			<FlyoutMenuItem isOpen={open}>
				<FlyoutMenuItemTrigger
					{...iconProps}
					onClick={(event) => onOpenChangeInternal({ isOpen: !open, event })}
				>
					{formatMessage(messages.reorderingMenuTriggerLabel)}
				</FlyoutMenuItemTrigger>
				<FlyoutMenuItemContent>
					<MenuList>
						<ButtonMenuItem
							isDisabled={!canMoveUp}
							onClick={(event) => {
								moveToTop();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveToTop)}
						</ButtonMenuItem>
						<ButtonMenuItem
							isDisabled={!canMoveUp}
							onClick={(event) => {
								moveUp();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveUp)}
						</ButtonMenuItem>
						<ButtonMenuItem
							isDisabled={!canMoveDown}
							onClick={(event) => {
								moveDown();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveDown)}
						</ButtonMenuItem>
						<ButtonMenuItem
							isDisabled={!canMoveDown}
							onClick={(event) => {
								moveToBottom();
								onOpenChangeInternal({ isOpen: false, event });
							}}
							elemBefore={COLLAPSE_ELEM_BEFORE}
						>
							{formatMessage(messages.moveToBottom)}
						</ButtonMenuItem>
					</MenuList>
				</FlyoutMenuItemContent>
			</FlyoutMenuItem>
		);
	}

	return (
		<DropdownMenu
			shouldRenderToParent
			spacing="compact"
			placement="right-start"
			trigger={({ triggerRef, ...triggerProps }: CustomTriggerProps) => {
				return (
					<TriggerComponent {...iconProps} {...triggerProps} ref={triggerRef} isDisabled={isOnly}>
						{formatMessage(messages.reorderingMenuTriggerLabel)}
					</TriggerComponent>
				);
			}}
			onOpenChange={onOpenChangeInternal}
			isOpen={isOnly ? false : open}
		>
			<DropdownItemGroup>
				<DropdownItem isDisabled={!canMoveUp} onClick={moveToTop}>
					{formatMessage(messages.moveToTop)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveUp} onClick={moveUp}>
					{formatMessage(messages.moveUp)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveDown} onClick={moveDown}>
					{formatMessage(messages.moveDown)}
				</DropdownItem>
				<DropdownItem isDisabled={!canMoveDown} onClick={moveToBottom}>
					{formatMessage(messages.moveToBottom)}
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
}

function NavItemReorderingMenuNew({
	TriggerComponent = ButtonMenuItem,
	canMoveUp,
	canMoveDown,
	moveUp,
	moveDown,
	moveToTop,
	moveToBottom,
	onOpenChange,
}: NavItemReorderingProps) {
	const { formatMessage } = useIntl();
	const isOnly = !canMoveUp && !canMoveDown;
	const [open, setOpen] = useState(false);
	const listItemRef = useRef<HTMLDivElement>(null);

	const onOpenChangeInternal = useCallback(
		(args: OnOpenChangeArgs) => {
			setOpen(args.isOpen);
			onOpenChange?.(args);
		},
		[onOpenChange],
	);

	useEffect(() => {
		// Remove role="listitem" from the root component within ButtonMenuItem.
		// We add our own wrapper with role around the whole thing, to ensure that the menu is rendered inside the listitem/menuitem semantically.
		// Otherwise, it would be a sibling to it without the appropriate role, which is both an a11y violation and doesn't make semantic sense.
		// Also, this allows us to account for the expected parent roles of all supported trigger types.
		if (TriggerComponent === ButtonMenuItem) {
			if (listItemRef.current) {
				listItemRef.current.removeAttribute('role');
			}
		}
	}, [TriggerComponent]);

	/**
	 * Set wrapper role based on the default semantics of the trigger component.
	 * This is just so we can shuffle around where the role attribute is defined
	 * to ensure semantics of the whole component are correct when the reordering menu is fully open.
	 * We aren't messing with the roles themselves at this point
	 * (e.g., making a ButtonMenuItem a "menuitem" not a "listitem"), though we could if there is a need to in the future.
	 */
	const wrapperRole = useMemo(() => {
		if (TriggerComponent === ButtonMenuItem) {
			return 'listitem';
		}

		return undefined;
	}, [TriggerComponent]);

	return (
		<div role={wrapperRole}>
			<DropdownMenu
				shouldRenderToParent
				spacing="compact"
				placement="right-start"
				trigger={({ triggerRef, ...triggerProps }: CustomTriggerProps) => {
					return (
						<TriggerComponent
							elemBefore={<GrowVerticalIcon label="" />}
							elemAfter={
								<ChevronRightIcon color={token('color.icon.subtle')} label="" size="small" />
							}
							{...triggerProps}
							ref={triggerRef}
							isDisabled={isOnly}
							listItemRef={listItemRef}
						>
							{formatMessage(messages.reorderingMenuTriggerLabel)}
						</TriggerComponent>
					);
				}}
				onOpenChange={onOpenChangeInternal}
				isOpen={isOnly ? false : open}
			>
				<DropdownItemGroup>
					<DropdownItem isDisabled={!canMoveUp} onClick={moveToTop}>
						{formatMessage(messages.moveToTop)}
					</DropdownItem>
					<DropdownItem isDisabled={!canMoveUp} onClick={moveUp}>
						{formatMessage(messages.moveUp)}
					</DropdownItem>
					<DropdownItem isDisabled={!canMoveDown} onClick={moveDown}>
						{formatMessage(messages.moveDown)}
					</DropdownItem>
					<DropdownItem isDisabled={!canMoveDown} onClick={moveToBottom}>
						{formatMessage(messages.moveToBottom)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</div>
	);
}

export const NavItemReorderingMenu = componentWithFG(
	'navx-2284-nav-item-actions-layout-and-semantics',
	NavItemReorderingMenuNew,
	NavItemReorderingMenuOld,
);
